import React, { useState, useEffect } from 'react';
import { OperatorId, ReceivingAccount } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { X, Copy, Check, ArrowRight, ShieldCheck, PhoneCall, Send, Wallet as WalletIcon, CheckCircle2 } from 'lucide-react';
import { OperatorLogo, PaymentLogo, OPERATOR_CONFIGS } from '../common/BrandLogo';

interface SellBillModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const DEFAULT_RECEIVERS: Record<OperatorId, { phone: string; instructions: string }> = {
  mpt: { phone: '09450001122', instructions: 'Transfer airtime via *223*amount*09450001122#' },
  atom: { phone: '09780001122', instructions: 'Transfer airtime via *979*2*amount*09780001122#' },
  mytel: { phone: '09690001122', instructions: 'Transfer airtime via *966*3*amount*09690001122#' },
  u9: { phone: '09970001122', instructions: 'Transfer airtime to 09970001122' },
};

export const SellBillModal: React.FC<SellBillModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useLanguage();
  const { isGuest } = useAuth();
  const [operatorId, setOperatorId] = useState<OperatorId>('mytel');
  const [amount, setAmount] = useState('10000');
  const [senderPhone, setSenderPhone] = useState('09');
  const [settlementType, setSettlementType] = useState<'wallet' | 'external_kpay' | 'external_wave'>('wallet');
  const [payoutDestination, setPayoutDestination] = useState('');
  const [quote, setQuote] = useState<{ ratePercentage: number; payoutAmount: number } | null>(null);
  const [receivingAccounts, setReceivingAccounts] = useState<ReceivingAccount[]>([]);
  const [submittedOrder, setSubmittedOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedType, setCopiedType] = useState<string | null>(null);

  // Fetch active receiving accounts for airtime
  useEffect(() => {
    api.getReceivingAccounts().then((accs) => {
      if (accs && accs.length > 0) {
        setReceivingAccounts(accs);
      }
    }).catch(() => {});
  }, []);

  // Fetch dynamic quote when operator or amount changes
  useEffect(() => {
    const num = parseInt(amount.replace(/\D/g, ''), 10) || 0;
    if (num >= 1000) {
      api.getBuybackQuote(operatorId, num).then((q) => {
        if (q) setQuote(q);
      }).catch(() => {});
    }
  }, [operatorId, amount]);

  // Determine active receiver phone and transfer instructions for selected operator
  const activeReceiver = receivingAccounts.find(
    (a) => a.type === 'airtime' && a.provider.toLowerCase() === operatorId.toLowerCase()
  );
  const receiverNumber = activeReceiver?.account_number || DEFAULT_RECEIVERS[operatorId]?.phone || '09450001122';
  const rawInstructions = activeReceiver?.instructions || DEFAULT_RECEIVERS[operatorId]?.instructions || '';
  const currentAmountClean = amount.replace(/\D/g, '') || 'amount';
  const formattedInstructions = rawInstructions.replace(/amount/gi, currentAmountClean);

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopiedType(type);
    setTimeout(() => setCopiedType(null), 2000);
  };

  const handleSubmit = async () => {
    setError('');
    const num = parseInt(amount.replace(/\D/g, ''), 10);
    if (!num || num < 2000) {
      setError('အနည်းဆုံး ၂,၀၀၀ ကျပ် ဘေလ်လွှဲပေးရပါမည်။');
      return;
    }

    if (!senderPhone || senderPhone.trim().length < 9) {
      setError('ဘေလ်လွှဲမည့် ဖုန်းနံပါတ် အပြည့်အစုံ ထည့်သွင်းပေးပါ။');
      return;
    }

    if (settlementType === 'external_kpay' && (!payoutDestination || payoutDestination.trim().length < 7)) {
      setError('ငွေထုတ်ယူမည့် KPay ဖုန်းနံပါတ် သို့မဟုတ် အကောင့် ထည့်သွင်းပေးပါ။');
      return;
    }

    if (settlementType === 'external_wave' && (!payoutDestination || payoutDestination.trim().length < 7)) {
      setError('ငွေထုတ်ယူမည့် WavePay ဖုန်းနံပါတ် သို့မဟုတ် အကောင့် ထည့်သွင်းပေးပါ။');
      return;
    }

    setLoading(true);
    try {
      const res = await api.createBuyback({
        operatorId,
        airtimeAmount: num,
        settlementType,
        senderPhone: senderPhone.trim(),
        payoutDestination: settlementType !== 'wallet' ? payoutDestination.trim() : undefined,
      });
      setSubmittedOrder(res.buyback || {
        id: `bb-${Date.now()}`,
        airtime_amount: num,
        payout_amount: quote?.payoutAmount || 0,
        settlement_type: settlementType,
        payout_destination: payoutDestination.trim(),
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Sell bill submission failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-surface-card w-full max-w-md rounded-t-3xl sm:rounded-3xl border border-plum-border shadow-2xl overflow-hidden max-h-[92vh] flex flex-col animate-in slide-in-from-bottom-5">
        {/* Header */}
        <div className="px-5 py-4 border-b border-plum-border flex items-center justify-between">
          <h2 className="font-bold text-plum-dark text-base">{t.sellBill}</h2>
          <button
            onClick={() => {
              if (submittedOrder) onSuccess();
              onClose();
            }}
            className="p-1.5 rounded-full hover:bg-surface-page text-plum-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {isGuest ? (
            <div className="py-6 text-center space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto">
                <Send className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-plum-dark text-sm">Telegram Bot မှတစ်ဆင့် ဘေလ်ရောင်းပါ</h3>
              <p className="text-xs text-plum-muted leading-relaxed max-w-xs mx-auto">
                သင်၏ မိုဘိုင်းဖုန်းဘေလ်များကို ရောင်းချပြီး KPay၊ WavePay သို့မဟုတ် PluvP Wallet ဖြင့် ငွေထုတ်ယူရန် Telegram Bot မှတစ်ဆင့် ဝင်ရောက်ပေးပါ။
              </p>
              <a
                href="https://t.me/pluvp_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 w-full py-2.5 px-4 bg-primary text-white rounded-xl text-xs font-semibold hover:bg-primary-hover transition shadow-sm"
              >
                <Send className="w-4 h-4" />
                <span>Telegram Bot တွင် ဖွင့်မည်</span>
              </a>
            </div>
          ) : submittedOrder ? (
            /* Post-Submission Success View with copyable phone */
            <div className="py-4 space-y-4">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-green-500/15 text-status-green flex items-center justify-center mx-auto border border-green-500/30">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h3 className="font-bold text-plum-dark text-base">ဘေလ်ရောင်းချလွှာ တင်သွင်းပြီးပါပြီ</h3>
                <p className="text-xs text-plum-muted">
                  Request ID: <span className="font-mono font-bold text-plum-dark">{submittedOrder.id}</span>
                </p>
              </div>

              {/* Prominent Receiver Number Reminder */}
              <div className="p-4 bg-surface-accent rounded-2xl border border-primary/30 space-y-3">
                <div className="flex items-center space-x-2">
                  <OperatorLogo id={operatorId} size="xs" className="rounded-md" />
                  <span className="text-xs font-bold text-plum-dark">
                    {OPERATOR_CONFIGS[operatorId].name} ဘေလ်လွှဲပြောင်းပေးရမည့်နံပါတ်
                  </span>
                </div>

                <div className="bg-surface-page p-3 rounded-xl border border-plum-border flex items-center justify-between">
                  <div>
                    <span className="text-[10px] text-plum-muted block">ဘေလ်လက်ခံမည့်ဖုန်း</span>
                    <span className="font-mono font-bold text-base text-plum-dark">{receiverNumber}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(receiverNumber, 'post-submit')}
                    className="px-3 py-1.5 rounded-xl bg-primary hover:bg-primary-hover text-white text-xs font-semibold flex items-center space-x-1.5 transition active:scale-95 shadow-xs"
                  >
                    {copiedType === 'post-submit' ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-status-green" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>

                {formattedInstructions && (
                  <div className="p-2.5 rounded-xl bg-surface-page/80 border border-plum-border/60 flex items-center justify-between text-xs">
                    <div className="space-y-0.5 mr-2 flex-1">
                      <span className="text-[10px] text-plum-muted block">ဘေလ်လွှဲရန် ကုဒ် (USSD):</span>
                      <code className="font-mono font-bold text-xs text-primary block break-all">
                        {formattedInstructions}
                      </code>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopy(formattedInstructions, 'post-ussd')}
                      className="p-1.5 rounded-lg border border-plum-border hover:bg-surface-page text-plum-muted hover:text-primary transition shrink-0 active:scale-95"
                      title="Copy transfer code"
                    >
                      {copiedType === 'post-ussd' ? (
                        <Check className="w-3.5 h-3.5 text-status-green" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Order Summary Details */}
              <div className="bg-surface-page p-3.5 rounded-2xl border border-plum-border space-y-2 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-plum-muted">လွှဲပေးရမည့် ဘေလ်ပမာဏ:</span>
                  <span className="font-mono font-bold text-plum-dark">
                    {(submittedOrder.airtime_amount || 0).toLocaleString()} Ks
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-plum-border/40">
                  <span className="text-plum-muted">ပြန်လည်ရရှိမည့်ငွေ:</span>
                  <span className="font-mono font-bold text-primary text-sm">
                    {(submittedOrder.payout_amount || 0).toLocaleString()} Ks
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-plum-border/40">
                  <span className="text-plum-muted">ထုတ်ယူမည့်နည်းလမ်း:</span>
                  <span className="font-bold text-plum-dark flex items-center space-x-1">
                    {submittedOrder.settlement_type === 'external_kpay' && (
                      <span className="text-blue-600">KPay ({submittedOrder.payout_destination})</span>
                    )}
                    {submittedOrder.settlement_type === 'external_wave' && (
                      <span className="text-amber-600">WavePay ({submittedOrder.payout_destination})</span>
                    )}
                    {submittedOrder.settlement_type === 'wallet' && (
                      <span className="text-primary">PluvP Wallet</span>
                    )}
                  </span>
                </div>
              </div>

              <p className="text-[11px] text-plum-muted text-center leading-relaxed">
                • အထက်ပါနံပါတ်သို့ ဘေလ်မလွှဲရသေးပါက လွှဲပေးပါ။<br />
                • ဆိုင်ဘက်မှ ဘေလ်ရောက်ရှိမှုကို စစ်ဆေးပြီးပါက မိနစ်ပိုင်းအတွင်း ငွေထုတ်ပေးပါမည်။
              </p>

              <button
                type="button"
                onClick={() => {
                  onSuccess();
                  onClose();
                }}
                className="w-full bg-primary hover:bg-primary-hover text-white font-bold py-3 rounded-xl transition shadow-xs touch-target text-sm"
              >
                နားလည်ပါပြီ (Close)
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="p-3 bg-red-500/15 border border-red-500/30 rounded-xl text-xs text-red-400">
                  {error}
                </div>
              )}

              {/* Operator selector */}
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-2">
                  Operator ရွေးပါ
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {(['mpt', 'atom', 'u9', 'mytel'] as OperatorId[]).map((op) => {
                    const isSel = operatorId === op;
                    const cfg = OPERATOR_CONFIGS[op];
                    return (
                      <button
                        key={op}
                        type="button"
                        onClick={() => setOperatorId(op)}
                        title={cfg.name}
                        aria-label={cfg.name}
                        className={`py-2.5 px-1.5 rounded-xl border flex flex-col items-center justify-center space-y-1.5 transition touch-target group ${
                          isSel
                            ? `bg-surface-card ring-2 shadow-xs ${cfg.selectedRing}`
                            : 'border-plum-border bg-surface-page hover:border-plum-border/80'
                        } active:scale-95`}
                      >
                        <OperatorLogo
                          id={op}
                          size="md"
                          className={`rounded-xl shadow-xs transition-transform group-hover:scale-105 ${
                            isSel ? 'scale-105' : ''
                          }`}
                        />
                        <span
                          className={`text-xs font-bold leading-none transition-colors ${
                            isSel ? 'text-plum-dark' : 'text-plum-muted group-hover:text-plum-dark'
                          }`}
                        >
                          {cfg.name.split(' ')[0]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Airtime amount */}
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">
                  {t.enterAmount} (Ks)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-surface-page border border-plum-border rounded-xl px-3.5 py-3 text-lg font-bold font-mono text-plum-dark focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* Quote Card */}
              {quote && (
                <div className="p-4 bg-surface-accent rounded-2xl border border-primary/20 space-y-2 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-plum-muted">{t.rate}:</span>
                    <span className="font-bold text-primary text-sm">{quote.ratePercentage}%</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-primary/10">
                    <span className="text-plum-dark font-medium">{t.payoutAmount}:</span>
                    <span className="font-bold text-base text-primary">
                      {quote.payoutAmount.toLocaleString()} Ks
                    </span>
                  </div>
                </div>
              )}

              {/* Prominently Displayed Receiving Phone Number Card with Copy Button */}
              <div className="p-3.5 bg-surface-accent rounded-2xl border border-primary/30 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <OperatorLogo id={operatorId} size="xs" className="rounded-md" />
                    <span className="text-xs font-bold text-plum-dark">
                      {OPERATOR_CONFIGS[operatorId].name} ဘေလ်လွှဲပြောင်းပေးရမည့်နံပါတ်
                    </span>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-primary font-bold">
                    Receiver SIM
                  </span>
                </div>

                <div className="bg-surface-page p-3 rounded-xl border border-plum-border flex items-center justify-between">
                  <div className="space-y-0.5">
                    <span className="text-[10px] text-plum-muted block">ဘေလ်လက်ခံမည့် ဖုန်းနံပါတ်</span>
                    <span className="font-mono font-bold text-base text-plum-dark tracking-wider">
                      {receiverNumber}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(receiverNumber, 'receiver')}
                    className="px-3 py-1.5 rounded-xl bg-primary hover:bg-primary-hover text-white text-xs font-semibold flex items-center space-x-1.5 transition active:scale-95 shadow-xs touch-target"
                    title="Copy receiving phone number"
                  >
                    {copiedType === 'receiver' ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-status-green" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>

                {formattedInstructions && (
                  <div className="p-2.5 rounded-xl bg-surface-page border border-plum-border/60 flex items-center justify-between text-xs">
                    <div className="space-y-0.5 mr-2 flex-1">
                      <span className="text-[10px] text-plum-muted block">ဘေလ်လွှဲရန် USSD ကုဒ်:</span>
                      <code className="font-mono font-bold text-xs text-primary block break-all">
                        {formattedInstructions}
                      </code>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopy(formattedInstructions, 'ussd')}
                      className="p-1.5 rounded-lg border border-plum-border hover:bg-surface-page text-plum-muted hover:text-primary transition shrink-0 active:scale-95"
                      title="Copy transfer code"
                    >
                      {copiedType === 'ussd' ? (
                        <Check className="w-3.5 h-3.5 text-status-green" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                )}

                <p className="text-[11px] text-plum-muted leading-relaxed">
                  💡 အထက်ပါနံပါတ်သို့ ဘေလ်အရင်လွှဲပြီးပါက အောက်တွင် မိမိဖုန်းနံပါတ်နှင့် ငွေလက်ခံမည့်အကောင့် ထည့်သွင်းပေးပါ။
                </p>
              </div>

              {/* Sender Phone */}
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1">
                  {t.senderPhone} (ဘေလ်လွှဲမည့် မိမိဖုန်းနံပါတ်)
                </label>
                <input
                  type="tel"
                  value={senderPhone}
                  onChange={(e) => setSenderPhone(e.target.value)}
                  placeholder="09xxxxxxxxx"
                  className="w-full bg-surface-page border border-plum-border rounded-xl px-3.5 py-2.5 text-sm font-mono text-plum-dark focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* Settlement Option - Wallet, External KPay, External WavePay */}
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-2">
                  {t.settlementDestination}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setSettlementType('wallet')}
                    className={`py-2.5 px-2 rounded-xl border text-xs font-bold transition flex flex-col items-center justify-center space-y-1.5 touch-target ${
                      settlementType === 'wallet'
                        ? 'border-primary bg-surface-accent text-primary ring-2 ring-primary/20 shadow-xs'
                        : 'border-plum-border bg-surface-page text-plum-muted hover:border-plum-border/80'
                    } active:scale-95`}
                  >
                    <div className="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <WalletIcon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[11px] truncate">PluvP Wallet</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSettlementType('external_kpay')}
                    className={`py-2.5 px-2 rounded-xl border text-xs font-bold transition flex flex-col items-center justify-center space-y-1.5 touch-target ${
                      settlementType === 'external_kpay'
                        ? 'border-blue-500 bg-blue-50/40 text-blue-700 ring-2 ring-blue-400/20 shadow-xs'
                        : 'border-plum-border bg-surface-page text-plum-muted hover:border-plum-border/80'
                    } active:scale-95`}
                  >
                    <PaymentLogo method="kpay" size="xs" className="rounded-md" />
                    <span className="text-[11px] truncate">KPay</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSettlementType('external_wave')}
                    className={`py-2.5 px-2 rounded-xl border text-xs font-bold transition flex flex-col items-center justify-center space-y-1.5 touch-target ${
                      settlementType === 'external_wave'
                        ? 'border-amber-500 bg-amber-50/40 text-amber-700 ring-2 ring-amber-400/20 shadow-xs'
                        : 'border-plum-border bg-surface-page text-plum-muted hover:border-plum-border/80'
                    } active:scale-95`}
                  >
                    <PaymentLogo method="wavepay" size="xs" className="rounded-md" />
                    <span className="text-[11px] truncate">WavePay</span>
                  </button>
                </div>
              </div>

              {settlementType === 'external_kpay' && (
                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">
                    KPay ဖုန်းနံပါတ် (ငွေလက်ခံမည့် KPay အကောင့်)
                  </label>
                  <input
                    type="tel"
                    value={payoutDestination}
                    onChange={(e) => setPayoutDestination(e.target.value)}
                    placeholder="09xxxxxxxxx"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3.5 py-2.5 text-sm font-mono text-plum-dark focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              )}

              {settlementType === 'external_wave' && (
                <div>
                  <label className="block text-xs font-semibold text-plum-dark mb-1">
                    WavePay ဖုန်းနံပါတ် (ငွေလက်ခံမည့် Wave အကောင့်)
                  </label>
                  <input
                    type="tel"
                    value={payoutDestination}
                    onChange={(e) => setPayoutDestination(e.target.value)}
                    placeholder="09xxxxxxxxx"
                    className="w-full bg-surface-page border border-plum-border rounded-xl px-3.5 py-2.5 text-sm font-mono text-plum-dark focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              )}

              <button
                type="button"
                disabled={loading || senderPhone.length < 9}
                onClick={handleSubmit}
                className="w-full bg-primary hover:bg-primary-dark disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition shadow-xs touch-target text-sm"
              >
                {loading ? 'တင်သွင်းနေပါသည်...' : 'ဘေလ်ရောင်းလွှာ အတည်ပြုတင်မည်'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
